'use client';

import * as React from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from "@/lib/utils";

interface EmbeddedDataTableProps<TData> {
  /** Column definitions */
  columns: ColumnDef<TData>[];
  /** Data array */
  data: TData[];
  /** Optional empty state message */
  emptyMessage?: string;
  /** Optional className for the table wrapper */
  className?: string;
  /** Enable sorting (default: false) */
  enableSorting?: boolean;
}

/**
 * Lightweight data table component for embedding in detail pages
 * Simplified version without pagination, filters, or complex features
 */
export function EmbeddedDataTable<TData>({
  columns,
  data,
  emptyMessage = 'No data available',
  className,
  enableSorting = false,
}: EmbeddedDataTableProps<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    onSortingChange: enableSorting ? setSorting : undefined,
    state: {
      sorting: enableSorting ? sorting : undefined,
    },
    enableSorting,
  });

  if (data.length === 0) {
    return (
      <div className="text-muted-foreground flex min-h-[200px] items-center justify-center rounded-md border">
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={cn('rounded-md border', className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} style={{ width: header.getSize() }}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
